/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-unused-expressions, react-hooks/exhaustive-deps */
// @ts-nocheck
import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
  useMemo,
} from 'react';
import merge from 'lodash/merge';
import { useSearchParams } from 'react-router-dom';
import { useRecoilValue, useRecoilState } from 'recoil';
import type { Dayjs } from 'dayjs';
import { useTheme } from '@mui/material/styles';

import {
  selectedHiroTabsState,
  userDetailsState,
  hiroETHState,
  lenseState,
  optionTypeState,
  candleDurationState,
  hiroLineTypeState,
  priceLineTypeState,
  hiroViewAlertsState,
  hiroShowPriceState,
  sumWindowState,
  hiroShowKeyLevels,
  bottomCandlesTypeState,
  hiroIndicatorsState,
  hiroTotalsEnabledPutsCallsDisabledState,
  hiroAllTradesEnabledState,
  hiroNextExpiryEnabledState,
  hiroEnableCandlesState,
  isInstitutionalLocalState,
  negativeTrendColorState,
  positiveTrendColorState,
  newTVHiroState,
  showChartTooltipState,
  hiroVersionState,
  hiroV3AvailableState,
  hiroVersionSetTimestampState,
  hiroSeriesColorsState,
} from 'states';

import { InitialHiroData, getPremarketSymbols } from 'util/hiro';
import { isBloomberg } from 'util/bloomberg';
import { getQueryDate, ET, dayjs } from 'util/shared/date';
import type {
  BottomCandlesType,
  HiroIndicator,
  HiroLense,
  HiroLineType,
  HiroSeriesColors,
  HiroTimeseries,
  RawSpotgammaLevels,
  UserDetails,
} from 'types';
import { ProductType, HiroUpdatingType, SymbolLineParams } from 'types';
import useSetSym from 'hooks/hiro/useSetSym';
import useUserDetails from 'hooks/user/useUserDetails';
import { DEFAULT_HIRO_SYMBOL } from 'config/hiro';

// settings that are common across all HIRO instances
interface HiroGlobalPersistentSettings {
  hiroVersion: number;
  enableHiroV2: boolean;

  hiroV3Available: boolean;
  enableHiroV3: boolean;

  // SymbolTabs
  hiroTabs: string[];

  userDetails?: UserDetails;

  // HiroChart (lightweight-charts / trading_view)
  isInstitutionalLocal: boolean | null;
}

export interface HiroPerInstPersistentSettings
  extends HiroGlobalPersistentSettings {
  sym: string;
  symbolHasExtendedHours: boolean;

  // Chart display state
  includeHiroETH: boolean;
  selectedLenses: { [value in HiroLense]: number };
  optionType: 'total' | 'pc';
  candleDuration: number;
  hiroUpdatingType: HiroUpdatingType;
  hiroLineType: HiroLineType;
  priceLineType: HiroLineType;
  hiroSeriesColors: HiroSeriesColors;
  showAlerts: boolean;
  showPrice: boolean;
  sumWindow: number;
  showHiroKeyLevels: boolean;

  bottomCandlesType: BottomCandlesType;

  // Indicators
  hiroIndicators: HiroIndicator[];
  hiroTotalsEnabledPutsCallsDisabled: boolean;
  hiroAllTradesEnabled: boolean;
  hiroNextExpiryEnabled: boolean;
  hiroRollingWindowSeconds: number;
  hiroEnableCandles: boolean;

  negativeTrendColor: string;
  positiveTrendColor: string;
}

export interface HiroContextValue extends HiroPerInstPersistentSettings {
  // non-persistent settings

  showChartTooltip: boolean;
  hiroSelectedSeriesList: {
    seriesName: string;
    symbolLineParams: SymbolLineParams;
  }[];

  // Dates
  endQueryDate: Dayjs;
  startQueryDate: Dayjs;
  lastSetDate?: Dayjs;

  // HiroTradingViewContainer / useHiroData
  hiroData: HiroTimeseries;
  sgData?: RawSpotgammaLevels;

  zoomTime?: Dayjs;

  bottomDrawerExpanded: boolean;

  update: (u: Partial<HiroContextValue>) => void;
  mode: 'standalone' | 'canvas';
  canvasId?: number;
  expandableContentButtonType?: string;
}

export const HiroContext = createContext<HiroContextValue | null>(null);

export const useHiroCtx = (): HiroContextValue => {
  const ctx = useContext(HiroContext);
  if (ctx == null) {
    throw new Error('useHiroCtx must be used within a HiroProvider');
  }
  return ctx;
};

interface HiroProviderProps {
  mode?: 'standalone' | 'canvas';

  config?: Partial<HiroPerInstPersistentSettings>;
  onConfigChange?: (patch: Partial<HiroPerInstPersistentSettings>) => void;

  canvasId?: number;
  expandableContentButtonType?: string;

  children: ReactNode;
}

export const HiroProvider = ({
  mode = 'standalone',
  config,
  onConfigChange,
  children,
  canvasId,
  expandableContentButtonType,
}: HiroProviderProps) => {
  const standaloneMode = mode === 'standalone';
  const { changeHiroChartSetting } = useUserDetails();

  ///////////////
  // global state
  ///////////////
  const [hiroVersionStateVal, setHiroVersion] =
    useRecoilState(hiroVersionState);
  const hiroVersionSetTimestamp = useRecoilValue(hiroVersionSetTimestampState);
  const hiroV3Available = useRecoilValue(hiroV3AvailableState);

  let hiroVersion = 3;
  if (
    // eslint-disable-next-line no-constant-condition, no-constant-binary-expression
    false &&
    hiroVersionStateVal != null &&
    hiroVersionSetTimestamp != null &&
    hiroVersionSetTimestamp > dayjs().tz(ET).startOf('day').valueOf()
  ) {
    hiroVersion = hiroVersionStateVal;
  }

  const [enableHiroV2StateVal, setEnableHiroV2] =
    useRecoilState(newTVHiroState);

  const enableHiroV2 =
    standaloneMode &&
    (hiroVersion == null || !hiroV3Available
      ? enableHiroV2StateVal
      : hiroVersion == 2);

  const enableHiroV3 = hiroV3Available && (!standaloneMode || hiroVersion == 3);

  // SymbolTabs
  const [hiroTabs, setHiroTabs] = useRecoilState(selectedHiroTabsState);

  const userDetails = useRecoilValue(userDetailsState);

  const [isInstitutionalLocal, setInstitutionalLocal] = useRecoilState(
    isInstitutionalLocalState,
  );

  /////////////////
  // per inst state
  /////////////////

  const { getSym, setSym } = useSetSym();
  const [searchParams] = useSearchParams();
  const sym = searchParams.get('sym') ?? getSym(ProductType.HIRO);

  const symbolHasExtendedHours = getPremarketSymbols(ProductType.HIRO).has(
    config?.sym ?? sym,
  );

  // Chart display state
  const includeHiroETH = useRecoilValue(hiroETHState);
  const setIncludeHiroETH = useCallback(
    (v: boolean) => changeHiroChartSetting({ showExtendedHours: v }),
    [],
  );

  const selectedLenses = useRecoilValue(lenseState);
  const setSelectedLenses = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
    (lense: any) => changeHiroChartSetting({ selectedLenses: lense }),
    [],
  );
  const optionType = useRecoilValue(optionTypeState);
  const setOptionType = useCallback(
    (v: 'total' | 'pc') => changeHiroChartSetting({ optionType: v }),
    [],
  );
  const candleDuration = useRecoilValue(candleDurationState);
  // recoil updates are delayed from standard react useState updates, causing
  // issues with setting 1d/5d buttons, thus mirroring to create co-incidence
  const [candleDurationRecoilMirror, setCandleDurationRecoilMirror] =
    useState(candleDuration);
  const setCandleDuration = useCallback((v: number) => {
    setCandleDurationRecoilMirror(v);
    changeHiroChartSetting({
      candleDurationSecs: v,
    });
  }, []);
  const hiroUpdatingType = isBloomberg()
    ? HiroUpdatingType.STREAMING
    : HiroUpdatingType.POLLING;

  const hiroLineType = useRecoilValue(hiroLineTypeState);
  const setHiroLineType = useCallback(
    (v: HiroLineType) => changeHiroChartSetting({ hiroLineType: v }),
    [],
  );
  const priceLineType = useRecoilValue(priceLineTypeState);
  const setPriceLineType = useCallback(
    (v: HiroLineType) => changeHiroChartSetting({ priceLineType: v }),
    [],
  );
  const theme = useTheme();
  const [hiroSeriesColors, setHiroSeriesColors_] = useRecoilState(
    hiroSeriesColorsState,
  );

  const setHiroSeriesColors = useCallback(
    (v: Partial<HiroSeriesColors>) => {
      if (v == null) {
        setHiroSeriesColors_(null);
      } else {
        setHiroSeriesColors_(merge({}, hiroSeriesColors ?? {}, v));
      }
    },
    [hiroSeriesColors, setHiroSeriesColors_],
  );

  const showAlerts = useRecoilValue(hiroViewAlertsState);
  const setShowAlerts = useCallback(
    (v: boolean) => changeHiroChartSetting({ showAlerts: v }),
    [],
  );
  const showPrice = useRecoilValue(hiroShowPriceState);
  const setShowPrice = useCallback(
    (v: boolean) => changeHiroChartSetting({ showPrice: v }),
    [],
  );
  const sumWindow = useRecoilValue(sumWindowState);
  const setSumWindow = useCallback(
    (v: number) => changeHiroChartSetting({ sumWindow: v }),
    [],
  );
  const showHiroKeyLevels = useRecoilValue(hiroShowKeyLevels);
  const setShowHiroKeyLevels = useCallback(
    (v: boolean) => changeHiroChartSetting({ showKeyLevels: v }),
    [],
  );

  const bottomCandlesType = useRecoilValue(bottomCandlesTypeState);
  const setBottomCandlesType = useCallback(
    (v: BottomCandlesType) => changeHiroChartSetting({ bottomCandlesType: v }),
    [],
  );

  // Indicators
  const [hiroIndicators, setHiroIndicators] =
    useRecoilState(hiroIndicatorsState);

  const [
    hiroTotalsEnabledPutsCallsDisabled,
    setHiroTotalsEnabledPutsCallsDisabled,
  ] = useRecoilState(hiroTotalsEnabledPutsCallsDisabledState);
  const [hiroAllTradesEnabled, setHiroAllTradesEnabled] = useRecoilState(
    hiroAllTradesEnabledState,
  );
  const [hiroNextExpiryEnabled, setHiroNextExpiryEnabled] = useRecoilState(
    hiroNextExpiryEnabledState,
  );
  const [hiroEnableCandles, setHiroEnableCandles] = useRecoilState(
    hiroEnableCandlesState,
  );

  // HiroChart (lightweight-charts / trading_view)
  const negativeTrendColor = useRecoilValue(negativeTrendColorState);
  const positiveTrendColor = useRecoilValue(positiveTrendColorState);

  //////////////////
  // ephemeral state
  //////////////////
  const showChartTooltip = useRecoilValue(showChartTooltipState);
  const setShowChartTooltip = useCallback(
    (v: boolean) => changeHiroChartSetting({ showChartTooltip: v }),
    [],
  );
  const [hiroSelectedSeriesList, setHiroSelectedSeriesList] = useState([]);

  const defaultQueryDate = useMemo((): Dayjs => {
    const supportsPremarket =
      getPremarketSymbols(ProductType.HIRO).has(DEFAULT_HIRO_SYMBOL) &&
      includeHiroETH;
    return getQueryDate(supportsPremarket);
  });
  const [startQueryDate, setStartQueryDate] = useState(defaultQueryDate);
  const [endQueryDate, setEndQueryDate] = useState(defaultQueryDate);
  const [lastSetDate, setLastSetDate] = useState(undefined);

  // HiroTradingViewContainer / useHiroData
  const [hiroData, setHiroData] = useState(InitialHiroData);
  const [sgData, setSgData] = useState(undefined);

  const [zoomTime, setZoomTime] = useState(undefined);

  const [bottomDrawerExpanded, setBottomDrawerExpanded] = useState(false);

  const update = useCallback(
    (u: Partial<HiroContextValue>) => {
      for (const [k, v] of Object.entries(u)) {
        const update = () => onConfigChange({ [k]: v });
        switch (k) {
          // global
          case 'hiroVersion':
            setHiroVersion(v);
            break;
          case 'enableHiroV2':
            setEnableHiroV2(v);
            break;
          case 'enableHiroV3':
            break;
          case 'hiroTabs':
            setHiroTabs(v);
            break;
          case 'userDetails':
            break;
          case 'isInstitutionalLocal':
            setInstitutionalLocal(v);
            break;

          // per-inst
          case 'sym':
            standaloneMode ? setSym(v, ProductType.HIRO) : update();
            break;
          case 'includeHiroETH':
          case 'showExtendedHours':
            standaloneMode ? setIncludeHiroETH(v) : update();
            break;
          case 'selectedLenses':
            standaloneMode ? setSelectedLenses(v) : update();
            break;
          case 'optionType':
            standaloneMode ? setOptionType(v) : update();
            break;
          case 'candleDuration':
            standaloneMode ? setCandleDuration(v) : update();
            break;
          case 'hiroUpdatingType':
            //standaloneMode ? setHiroUpdatingType(v) : update();
            break;
          case 'hiroLineType':
            standaloneMode ? setHiroLineType(v) : update();
            break;
          case 'priceLineType':
            standaloneMode ? setPriceLineType(v) : update();
            break;
          case 'hiroSeriesColors':
            if (standaloneMode) {
              setHiroSeriesColors(v);
            } else {
              if (v == null) {
                update();
              } else {
                onConfigChange({ [k]: merge({}, config[k], v) });
              }
            }
            break;
          case 'showAlerts':
            standaloneMode ? setShowAlerts(v) : update();
            break;
          case 'showPrice':
            standaloneMode ? setShowPrice(v) : update();
            break;
          case 'hiroRollingWindowSeconds':
            standaloneMode ? setSumWindow(v) : update();
            break;
          case 'showHiroKeyLevels':
          case 'showKeyLevels':
            standaloneMode ? setShowHiroKeyLevels(v) : update();
            break;
          case 'bottomCandlesType':
            standaloneMode ? setBottomCandlesType(v) : update();
            break;
          case 'hiroIndicators':
            standaloneMode ? setHiroIndicators(v) : update();
            break;
          case 'hiroTotalsEnabledPutsCallsDisabled':
            standaloneMode
              ? setHiroTotalsEnabledPutsCallsDisabled(v)
              : update();
            break;
          case 'hiroAllTradesEnabled':
            standaloneMode ? setHiroAllTradesEnabled(v) : update();
            break;
          case 'hiroNextExpiryEnabled':
            standaloneMode ? setHiroNextExpiryEnabled(v) : update();
            break;
          case 'hiroEnableCandles':
            standaloneMode ? setHiroEnableCandles(v) : update();
            break;
          case 'negativeTrendColor':
            //standaloneMode ? setNegativeTrendColor(v) : update();
            break;
          case 'positiveTrendColor':
            //standaloneMode ? setPositiveTrendColor(v) : update();
            break;

          // ephemeral
          case 'showChartTooltip':
            setShowChartTooltip(v);
            break;
          case 'hiroSelectedSeriesList':
            setHiroSelectedSeriesList(v);
            break;
          case 'endQueryDate':
            setEndQueryDate(v);
            break;
          case 'startQueryDate':
            setStartQueryDate(v);
            break;
          case 'lastSetDate':
            setLastSetDate(v);
            break;
          case 'hiroData':
            setHiroData(v);
            break;
          case 'sgData':
            setSgData(v);
            break;
          case 'zoomTime':
            setZoomTime(v);
            break;
          case 'bottomDrawerExpanded':
            setBottomDrawerExpanded(v);
            break;
          default:
            console.warn(`Unknown update field '${k}'`);
        }
      }
    },
    [
      config,
      onConfigChange,
      standaloneMode,
      setHiroVersion,
      setEnableHiroV2,
      setHiroTabs,
      setInstitutionalLocal,
      setIncludeHiroETH,
      setSelectedLenses,
      setOptionType,
      setCandleDuration,
      setHiroLineType,
      setPriceLineType,
      setHiroSeriesColors,
      setShowAlerts,
      setShowPrice,
      setSumWindow,
      setShowHiroKeyLevels,
      setBottomCandlesType,
      setHiroIndicators,
      setHiroTotalsEnabledPutsCallsDisabled,
      setHiroAllTradesEnabled,
      setHiroNextExpiryEnabled,
      setHiroEnableCandles,
      setShowChartTooltip,
      setHiroSelectedSeriesList,
      setLastSetDate,
      setHiroData,
      setSgData,
      setZoomTime,
      setBottomDrawerExpanded,
      setStartQueryDate,
      setEndQueryDate,
    ],
  );

  return (
    <HiroContext.Provider
      value={{
        // global settings
        hiroVersion,
        enableHiroV2,
        enableHiroV3,
        hiroTabs,
        userDetails,
        isInstitutionalLocal,

        // per inst settings
        sym: config?.sym ?? sym,
        symbolHasExtendedHours,
        includeHiroETH:
          (!enableHiroV3 || symbolHasExtendedHours) &&
          (config?.includeHiroETH ?? includeHiroETH),
        selectedLenses: config?.selectedLenses ?? selectedLenses,
        optionType: config?.optionType ?? optionType,
        candleDuration:
          config?.candleDuration ??
          candleDurationRecoilMirror ??
          candleDuration,
        hiroUpdatingType: config?.hiroUpdatingType ?? hiroUpdatingType,
        hiroLineType: config?.hiroLineType ?? hiroLineType,
        priceLineType: config?.priceLineType ?? priceLineType,
        hiroSeriesColors: merge(
          {},
          theme.palette.hiro.lenses,
          config?.hiroSeriesColors ?? hiroSeriesColors,
        ),
        showAlerts: config?.showAlerts ?? showAlerts,
        showPrice: config?.showPrice ?? showPrice,
        showHiroKeyLevels: config?.showHiroKeyLevels ?? showHiroKeyLevels,
        bottomCandlesType: config?.bottomCandlesType ?? bottomCandlesType,
        hiroIndicators: config?.hiroIndicators ?? hiroIndicators,
        hiroTotalsEnabledPutsCallsDisabled:
          config?.hiroTotalsEnabledPutsCallsDisabled ??
          hiroTotalsEnabledPutsCallsDisabled,
        hiroAllTradesEnabled:
          config?.hiroAllTradesEnabled ?? hiroAllTradesEnabled,
        hiroNextExpiryEnabled:
          config?.hiroNextExpiryEnabled ?? hiroNextExpiryEnabled,
        hiroRollingWindowSeconds: config?.hiroRollingWindowSeconds ?? sumWindow,
        hiroEnableCandles: config?.hiroEnableCandles ?? hiroEnableCandles,
        negativeTrendColor: config?.negativeTrendColor ?? negativeTrendColor,
        positiveTrendColor: config?.positiveTrendColor ?? positiveTrendColor,

        // ephemeral settings
        showChartTooltip,
        hiroSelectedSeriesList,
        endQueryDate,
        startQueryDate,
        lastSetDate,
        hiroData,
        sgData,
        zoomTime,
        bottomDrawerExpanded,

        update,
        mode,
        canvasId,
        expandableContentButtonType,
      }}
    >
      {children}
    </HiroContext.Provider>
  );
};
